import type { IconType } from "~/common/components/icon/IconType";
import { type CMSComponentProps, type CMSSectionComponentProps } from "~/features/cms/types/props";
import type ComponentTypes from "~/features/cms/componentTypes";
import type { BEButtonSizes } from "~/features/cms/utils/helpers";
import type { TableType } from "~/features/cms/homepagePersonalization/components/OrderTable";

export enum TeaserType {
  STANDARD = "STANDARD",
  DROPDOWN = "DROPDOWN",
  CONTACT = "CONTACT",
  CTA = "CTA",
  PUBLICATION = "PUBLICATION",
}

export interface CMSLinkUrlDTO {
  url: string;
  language: string;
}

/** Base properties shared by all teaser DTOs */
interface CMSTeaserBaseDTO {
  headline?: string;
  subHeadline?: string;
  linkUrl?: CMSLinkUrlDTO[]; // or Array in case of multi-lingual links for the PUBLICATION element
  linkLabel?: string;
  buttonLabel?: string; // same as `linkLabel` ?
  medias?: SchaefflerMediaDTO[];
  imageClickable?: boolean;
  buttonType?: "PRIMARY" | "SECONDARY" | "TEXT";
  buttonSize?: BEButtonSizes;
  teaserType?: TeaserType;
  target?: string;
  contentDropdown?: CMSSectionComponentProps;
  mirrored?: boolean;
  fullWidth?: boolean;
}

/** Standard teaser DTO with literal type */
export interface CMSTeaserStandardDTO extends CMSTeaserBaseDTO {
  type: ComponentTypes.CMSTeaser;
}

/** Publication teaser DTO with literal type */
export interface CMSTeaserPublicationDTO extends CMSTeaserBaseDTO {
  type: ComponentTypes.CMSPublicationTeaser;
}

/** Union type for backward compatibility */
export type CMSTeaserDTO = CMSTeaserStandardDTO | CMSTeaserPublicationDTO;

export interface CMSQuoteDTO {
  type: ComponentTypes.CMSQuote;
  quote?: string;
  name?: string;
  avatarUrl?: string;
  subtitle?: string;
  btnLabel?: string;
  linkUrl?: CMSLinkUrlDTO[]; // or Array in case of multi-lingual links for the PUBLICATION element
  target?: string;
  rootStyles?: string;
  quoteStyles?: string;
  nameStyles?: string;
  subtitleStyles?: string;
}

export enum AccessLevel {
  general = "GENERAL",
  plus = "PLUS",
  business = "BUSINESS",
  plusBusiness = "PLUS-BUSINESS",
}

export interface CMSStandaloneImageDTO {
  id: string;
  type: ComponentTypes.CMSStandaloneMedia;
  media?: SchaefflerMediaDTO;
  caption?: string;
  url?: string;
  target?: string;

  alignment?: "LEFT" | "CENTER" | "RIGHT";
  size?: "S100" | "S75" | "S50" | "S25";
}

export interface SchaefflerImageDTO {
  id: string;
  type: MediaType.image;
  ratio?: string;
  imageUrl?: string;
  mobileImageUrl?: string;
  alt?: string;
  caption?: string;
}

export interface SchaefflerVideoDTO {
  id: string;
  type: MediaType.video;
  ratio?: string;
  videoUrl?: string;
  disabledImage?: string;
  caption?: string;
}

export enum MediaType {
  video = "SchaefflerVideo",
  image = "SchaefflerImage",
}

export type SchaefflerMediaDTO = SchaefflerImageDTO | SchaefflerVideoDTO;

export interface LoginCallDTO {
  type: ComponentTypes.CMSLoginCall;
  accessLevel: AccessLevel;
  content: string;
}

export interface CMSColumnsContainerDTO {
  type: ComponentTypes.CMSColumnsContainer;
  htmlId: string;
  alignment: "LEFT" | "CENTER" | "RIGHT";
  columns: CMSColumnDTO[];
  rootStyles?: string;
}

export interface CMSColumnDTO {
  type: ComponentTypes.CMSColumn;
  widths: [number, number, number, number, number];
  children: CMSComponentProps[];
  rootStyles?: string;
}

export interface CMSPhotoDTO {
  type: ComponentTypes.CMSPhoto;
  body: string;
  imageUrl: string;
  openInNewTab: boolean;
  url: string;
  title: string;
  getWidth: (index: number) => string;
  getMargin: (relativeIndex: number, index: number) => string;
  getWidthParam: number;
  relativeIndex: number;
  index: number;
}

export interface CMSPhotoGalleryDTO {
  type: ComponentTypes.CMSPhotoGallery;
  images: CMSPhotoDTO[];
  rootStyles?: string;
}

export interface CMSColumnLinksDTO {
  type: ComponentTypes.CMSColumnLinks;
  headline: string;
  icon?: keyof typeof IconType;
  links: CMSColumnLinkDTO[];
  url: string;
}

export interface CMSColumnLinkDTO {
  type: ComponentTypes.CMSColumnLink;
  title: string;
  subtitle: string;
  url: string;
  urlType: "NEWTAB" | "SAMETAB" | "DOWNLOAD";
  index: number;
}

export interface CMSAccordionContainerDTO {
  type: ComponentTypes.CMSAccordionContainer;
  title: string;
  accordionItems: CMSAccordionDTO[];
}

export interface CMSAccordionDTO {
  type: ComponentTypes.CMSAccordion;
  title: string;
  accordionBody: CMSComponentProps[];
  openByDefault: boolean;
}

export interface CMSTableDTO {
  type: ComponentTypes.CMSTable;
  title: string;
  allowToPin: boolean;
  isSimpleView: boolean;
  rows: CMSTableRowDTO[];
  columns: CMSTableColumnDTO[];
  values: CMSTableValueDTO[];
}

export interface CMSTableColumnDTO {
  id: string;
  displayName: string;
  imageUrl?: string;
  pinned: boolean;
  seeMoreUrl?: string;
  isSimpleView: boolean;
  allowToPin: boolean;
  openInNewTab?: boolean;
}

export interface CMSTableRowDTO {
  id: string;
  displayName: string;
}

export interface CMSTableValueDTO {
  id: string;
  row: CMSTableRowDTO;
  column: CMSTableColumnDTO;
  value: string;
}

export interface CMSTableRowData {
  rowHeading: string;
  [id: string]: string;
}
export interface SchaefflerFormDTO {
  type: ComponentTypes.Form;
  name: string;
  hiddenFields: SchaefflerFormHiddenFieldDTO[];
  rows: CMSComponentProps[];
  onSuccess?: () => void;
  rootStyles?: string;
}
export interface SchaefflerFormHiddenFieldDTO {
  type: ComponentTypes.FormHiddenField;
  name: string;
  value: string;
}

export interface SchaefflerFormSelectFieldDTO {
  type: ComponentTypes.FormFieldSelect;
  name: string;
  required: boolean;
  title: string; // translation key
  values: SchaefflerFormSelectFieldOptionDTO[];
  initialValue?: string;
  rootStyles?: string;
}

export interface SchaefflerFormSelectFieldOptionDTO {
  type: ComponentTypes.FormFieldSelect;
  value: string;
  sfValue: string;
  label: string; // translation key
}

export type FieldTypes = "ALPHANUMERIC" | "NUMERIC" | "ALLCHARACTERS" | "EMAIL" | "PHONE" | "DATE" | "TEXTAREA";

export interface SchaefflerFormCheckboxFieldDTO {
  type: ComponentTypes.FormFieldCheckbox;
  name: string;
  required: boolean;
  title: string; // translation key
  fullTitle?: string;
  rootStyles?: string;
}

export interface SchaefflerFormTextFieldDTO {
  type: ComponentTypes.FormFieldText;
  name: string;
  fieldType: FieldTypes;
  required: boolean;
  title: string; // translation key
  maxLength?: number;
  initialValue?: string; // Initial value
  rootStyles?: string;
}

export interface CMSCardComponentDTO {
  type: ComponentTypes.CMSCardComponent;
  children: CMSComponentProps[];
  rootStyle: string;
  elevation?: number;
}

export interface CMSHomepagePersonalizationDTO {
  type: ComponentTypes.CMSHomepagePersonalization;
  chipsComponent: CMSChipLinkDTO[];
  title: string;
  recentComponents: CMSHomepagePersonalizationRecentDTO[];
  rotatingComponent: CMSRotatingBannersDTO;
}

export interface CMSRotatingBannersDTO {
  type: ComponentTypes.CMSRotatingBanners;
  banners: CMSBannerDTO[];
  timeout: number;
}

export interface CMSChipListDTO {
  type: ComponentTypes.CMSChipList;
  chipsComponent: CMSChipLinkDTO[];
}

export interface CMSChipLinkDTO {
  type: ComponentTypes.CMSChipLink;
  iconUrl: string;
  chip: {
    title: string;
    navigationEntry: {
      name: string;
      linkComponent: {
        name: string;
        url: string;
      };
    };
  };
}

export interface CMSBannerDTO {
  type: ComponentTypes.CMSBanner;
  headline: string;
  subheadline: string;
  linkLabel: string;
  imageUrl: string;
  target: string;
  linkUrl: string;
}

export interface CMSHomepagePersonalizationRecentDTO {
  type: ComponentTypes.CMSHomepagePersonalizationRecent;
  label: string;
  componentType: TableType.ORDERS | TableType.QUOTES;
}

export interface CMSKameleoonSlotDTO {
  type: ComponentTypes.CMSKameleoonSlot;
  featureFlagName: string;
  functionName: string;
}

export interface CMSIframeDTO {
  type: ComponentTypes.CMSIframe;
  url: string;
  enableIframeResize: boolean;
  rootStyle?: string;
}
